import type { Location } from "./types";
import { geoMe } from "./api";
import { readJson, readString, writeJson, writeString } from "./storage";

type GeoCache = {
  at: number; // epoch ms
  location: Location;
};

const GEO_CACHE_KEY = "ip_geo_cache";
const GEO_DISMISS_UNTIL_KEY = "ip_geo_dismiss_until";
const GEO_LAST_SUGGEST_KEY = "ip_geo_last_suggest"; // to avoid nag loops

const DEFAULT_CACHE_MAX_AGE_MS = 6 * 60 * 60 * 1000; // 6h
const MIN_NAG_GAP_MS = 6 * 60 * 60 * 1000; // 6h

function nowMs(): number {
  return Date.now();
}

function haversineKm(a: Location, b: Location): number {
  const R = 6371; // km
  const dLat = (b.latitude - a.latitude) * Math.PI / 180;
  const dLon = (b.longitude - a.longitude) * Math.PI / 180;
  const lat1 = a.latitude * Math.PI / 180;
  const lat2 = b.latitude * Math.PI / 180;
  const s =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.sin(dLon / 2) * Math.sin(dLon / 2) * Math.cos(lat1) * Math.cos(lat2);
  const c = 2 * Math.asin(Math.min(1, Math.sqrt(s)));
  return R * c;
}

function sameCountry(a: Location, b: Location): boolean {
  const ac = (a.countryCode || "").trim().toUpperCase();
  const bc = (b.countryCode || "").trim().toUpperCase();
  if (ac && bc) return ac === bc;
  return a.country.trim().toLowerCase() === b.country.trim().toLowerCase();
}

function shouldSuggestSwitch(saved: Location, detected: Location): boolean {
  if (!sameCountry(saved, detected)) return true;

  // Timezone change is a strong signal (travel / VPN exit)
  if (saved.timezone && detected.timezone && saved.timezone !== detected.timezone) {
    return true;
  }

  // Otherwise, distance-based (avoid tiny jitters).
  const km = haversineKm(saved, detected);
  return km >= 150;
}

export function dismissIpGeoSuggestion(hours = 24): void {
  const until = nowMs() + Math.max(1, hours) * 60 * 60 * 1000;
  writeString(GEO_DISMISS_UNTIL_KEY, String(until));
}

export function clearIpGeoDismiss(): void {
  writeString(GEO_DISMISS_UNTIL_KEY, "");
}

export async function detectLocationFromIp(forceFresh = false): Promise<Location | null> {
  const loc = await getIpGeoLocation({ forceFresh });
  return loc;
}

export async function getIpGeoLocation(opts?: { forceFresh?: boolean; maxAgeMs?: number }): Promise<Location | null> {
  const forceFresh = Boolean(opts?.forceFresh);
  const maxAgeMs = opts?.maxAgeMs ?? DEFAULT_CACHE_MAX_AGE_MS;

  if (!forceFresh) {
    const cached = readJson<GeoCache | null>(GEO_CACHE_KEY, null);
    if (cached && cached.location && typeof cached.at === "number") {
      if (nowMs() - cached.at <= maxAgeMs) return cached.location;
    }
  }

  try {
    const me = await geoMe();
    const location = me.location ?? null;
    if (location) {
      writeJson(GEO_CACHE_KEY, { at: nowMs(), location } satisfies GeoCache);
    }
    return location;
  } catch {
    return null;
  }
}

export async function checkRelocationSuggestion(current: Location): Promise<Location | null> {
  // Respect dismiss window
  const dismissUntil = Number(readString(GEO_DISMISS_UNTIL_KEY, "0")) || 0;
  if (dismissUntil && nowMs() < dismissUntil) return null;

  // Avoid nagging too frequently
  const lastSuggest = Number(readString(GEO_LAST_SUGGEST_KEY, "0")) || 0;
  if (lastSuggest && nowMs() - lastSuggest < MIN_NAG_GAP_MS) return null;

  // We want a fresher read for relocation checks
  const detected = await getIpGeoLocation({ forceFresh: true, maxAgeMs: 0 });
  if (!detected) return null;

  if (!shouldSuggestSwitch(current, detected)) return null;

  writeString(GEO_LAST_SUGGEST_KEY, String(nowMs()));
  return detected;
}

export function rememberAcceptedLocation(loc: Location): void {
  // keep cache aligned so subsequent loads are instant
  writeJson(GEO_CACHE_KEY, { at: nowMs(), location: loc } satisfies GeoCache);
  // clearing dismiss is OK because the user explicitly accepted
  clearIpGeoDismiss();
}
